import type { JSX } from 'react';
import { Code } from '@bookora/ui';

/**
 * «مریم احمدی — ۱۴۰۴-۰۱۴۲» — the way a patient is named in every picker.
 *
 * ── WHY IT IS NOT A TEMPLATE LITERAL ────────────────────────────────────────
 *
 * The file code is a digit-and-hyphen identifier, and the em-dash in front of
 * it is a bidi NEUTRAL. Inside an RTL popup that is the same shape as the
 * masked phone number in `staff/invites-panel.tsx`: the neutral picks up the
 * paragraph's direction and the runs on either side of it are laid out in the
 * wrong order. `<Code>` isolates the identifier so the sentence around it
 * cannot rearrange it — the panel's own convention wherever the code appears in
 * a table (`patients/patients-page.tsx`).
 *
 * `persianDigits` is ON here and OFF on the inventory's batch numbers, and the
 * difference is deliberate: a patient file code is read aloud and matched
 * against a Persian-printed file, while a batch number is compared character by
 * character with a Latin label on a vial.
 *
 * Every caller passes `filterOption={false}` or uses `FilterBar.Select`, so a
 * ReactNode label costs no local search behaviour; the search is the server's.
 *
 * Written as a plain builder rather than a component so that a select's
 * `options` array stays a data structure — which is what AntD wants — and so
 * this module has nothing for Fast Refresh to disagree about.
 */
export function patientOption(patient: { id: string; fullName: string; code?: string | null }): {
  value: string;
  label: JSX.Element;
} {
  return {
    value: patient.id,
    label: patient.code ? (
      <span>
        {patient.fullName} — <Code value={patient.code} persianDigits />
      </span>
    ) : (
      <span>{patient.fullName}</span>
    ),
  };
}
